import * as React from 'react';
import type { CommissionStatement } from '../../../MedicareProAPI/Entities/CommissionStatement/CommissionStatement';
import type { CommissionStatementResult } from '../../../MedicareProAPI/Entities/CommissionStatement/CommissionStatementResult';
import type {
  CommissionStatementModuleMode,
  CommissionStatementModuleName
} from '../../../MedicareProAPI/Model/CommissionStatement/CommissionStatementModule';
import { COMMISSION_STATEMENT_MODULE_TYPES } from '../../../MedicareProAPI/Model/CommissionStatement/CommissionStatementModule';
import {
  CommissionStatementRepository
} from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementRepository';
import { CommissionStatementResultRepository } from '../../../MedicareProAPI/Repositories/CommissionStatement/CommissionStatementResultRepository';
import { Utilities } from '../../../MedicareProAPI/Utilities/Utilities';
import StyledModal from '../../Components/StyledModal';
import { UserContext } from '../../Context/UserContext';
import { CommissionStatementProcessingResult } from './CommissionStatementProcessingResult';

export interface CommissionStatementFormProps {
  commissionStatement: CommissionStatement;
}

enum CommissionStatementFormProcessTab {
  PROCESS = 'process',
  PREVIOUS_RUNS = 'previous_runs',
}

export const CommissionStatementProcessing = (
  props: CommissionStatementFormProps
): React.JSX.Element => {
  const userData = React.useContext(UserContext);

  const [processMode, setProcessMode] = React.useState<CommissionStatementModuleMode>();
  const [processName, setProcessName] = React.useState<CommissionStatementModuleName>();
  const [processLoading, setProcessLoading] = React.useState<boolean>(false);
  const [processTab, setProcessTab] = React.useState<CommissionStatementFormProcessTab>(
    CommissionStatementFormProcessTab.PROCESS
  );
  const [commissionStatement, setFormCommissionStatement] = React.useState<CommissionStatement>();
  const [selectedModuleType, setSelectedModuleType] =
    React.useState<CommissionStatementModuleName>(null);
  const [commissionStatementResultsLoading, setCommissionStatementResultsLoading] =
    React.useState<boolean>(true);
  const [commissionStatementResults, setCommissionStatementResults] =
    React.useState<Array<CommissionStatementResult>>();
  const [commissionStatementResultsAggregated, setCommissionStatementResultsAggregated] =
    React.useState<Array<CommissionStatementResult>>();
  const [commissionStatementResult, setCommissionStatementResult] =
    React.useState<CommissionStatementResult>();

  const [runProcessModalShow, setRunProcessModalShow] = React.useState<boolean>(false);

  const commissionStatementResultRepository = new CommissionStatementResultRepository();
  commissionStatementResultRepository.setAccessToken(userData.userAccessToken);

  const commissionStatementRepository = new CommissionStatementRepository();
  commissionStatementRepository.setAccessToken(userData.userAccessToken);

  React.useEffect(() => {
    setFormCommissionStatement(props.commissionStatement);
    loadCommissionStatementResults(props.commissionStatement);
  }, [props.commissionStatement]);

  const loadCommissionStatementResults = (commissionStatement: CommissionStatement) => {
    commissionStatementResultRepository
      .getCommissionStatementResults(userData.userId, commissionStatement.getId())
      .then((commissionStatementResults) => {
        setCommissionStatementResultsLoading(false);
        setCommissionStatementResults(commissionStatementResults.getItems());

        const latestByName = new Map<string, CommissionStatementResult>();

        for (const item of commissionStatementResults.getItems()) {
          const name = item.getModule();
          const existing = latestByName.get(name);

          if (!existing || item.getDateTimeCreated() > existing.getDateTimeCreated()) {
            latestByName.set(name, item);
          }
        }

        const aggregatedResult = Array.from(latestByName.values());
        setCommissionStatementResultsAggregated(aggregatedResult);
      });
  };

  const getModuleName = (reference: CommissionStatementModuleName) => {
    const module = COMMISSION_STATEMENT_MODULE_TYPES.find((module) => module.name === reference);
    return module.label;
  }

  const startExecute = (
    moduleType: CommissionStatementModuleName
  ) => {
    runProcess(moduleType, 'execute');
  };

  const startPlan = (
    moduleType: CommissionStatementModuleName
  ) => {
    runProcess(moduleType, 'plan');
  };

  const runProcess = (
    moduleType: CommissionStatementModuleName,
    mode: CommissionStatementModuleMode = 'plan'
  ) => {
    setProcessLoading(true);
    setRunProcessModalShow(true);
    setProcessMode(mode);
    setProcessName(moduleType);

    commissionStatementRepository
      .runCommissionStatementModule(userData.userId, commissionStatement.getId(), moduleType, mode)
      .then((result) => {
        setProcessLoading(false);
        loadCommissionStatementResults(commissionStatement);
        setCommissionStatementResult(result);
      });
  };

  return (
    <>
      <StyledModal
        size="xl"
        title={processName ? getModuleName(processName) : "Run Process"}
        footerCloseButton={true}
        //show={runProcessModalShow}
        onClose={(e) => {
          setRunProcessModalShow(false);
        }}
      >
        {processLoading && (
          <div className="alert small alert-info" role="alert">
            <i className="fas fa-spinner fa-spin"></i> Processing your spreadsheet.
          </div>
        )}
        {(!processLoading && commissionStatementResult) && (<>
          {processMode === 'execute' ? (
            <p className="alert alert-success">The following changes have been applied.</p>

          ) : (<>
            <h5 className="font-weight-bold">Dry Run Results</h5>
            <p>These are the results of a dry run, no data has been added or updated in your CRM yet. If the results of the dry run look good, click Continue below</p>
          </>)}

          <CommissionStatementProcessingResult
            key={commissionStatementResult.getId()}
            module={commissionStatementResult.getModule()}
            data={commissionStatementResult.getData()}
            mode={processMode}
          />
          {
            processMode === 'plan' && (
              <button
                type="button"
                className="mt-3 btn btn-small btn-success float-right"
                onClick={e => startExecute(selectedModuleType)}
              >
                Continue
              </button>
            )
          }

        </>)}
      </StyledModal>
      {/*<div className="mt-2 btn-group btn-group-tabbed btn-group-toggle" data-toggle="buttons">
        <label
          className={`btn btn-primary${processTab === CommissionStatementFormProcessTab.PROCESS ? ' active' : ''}`}
          onClick={() => setProcessTab(CommissionStatementFormProcessTab.PROCESS)}
        >
          Run Process
        </label>
        <label
          className={`btn btn-primary${processTab === CommissionStatementFormProcessTab.PREVIOUS_RUNS ? ' active' : ''}`}
          onClick={() => setProcessTab(CommissionStatementFormProcessTab.PREVIOUS_RUNS)}
        >
          Previous Runs
        </label>
      </div>*/}

      <div className="border border-secondary p-2">
        {processTab === CommissionStatementFormProcessTab.PROCESS && (
          runProcessModalShow ? (
            <>
              {processLoading && (
                <div className="alert small alert-info" role="alert">
                  <i className="fas fa-spinner fa-spin"></i> Processing your spreadsheet.
                </div>
              )}
              {(!processLoading && commissionStatementResult) && (<>
                {processMode === 'execute' ? (
                  <p className="alert alert-success">The following changes have been applied.</p>

                ) : (<>
                  <h5 className="font-weight-bold">Dry Run Results</h5>
                  <p>These are the results of a dry run, no data has been added or updated in your CRM yet. If the results of the dry run look good, click Continue below</p>
                </>)}

                <CommissionStatementProcessingResult
                  key={commissionStatementResult.getId()}
                  module={commissionStatementResult.getModule()}
                  data={commissionStatementResult.getData()}
                  mode={processMode}
                />
                {
                  processMode === 'plan' && (
                    <button
                      type="button"
                      className="mt-3 btn btn-small btn-success float-right"
                      onClick={e => startExecute(selectedModuleType)}
                    >
                      Continue
                    </button>
                  )
                }

              </>)}

            </>
          ) : (
            <>
              <label className="label">Process to Run</label>
              <div className="row">
                {COMMISSION_STATEMENT_MODULE_TYPES.map((module) => {
                  const lastRun = commissionStatementResultsAggregated?.find((result => result.getModule() === module.name));

                  return (
                    <div className="col-sm-12 col-md-6 col-xl-4 mb-2">
                      <div className="border border-1 rounded p-3 h-100">
                        <h5 className="font-weight-bold">{module.label}</h5>
                        {lastRun && (
                          <span className="badge badge-pill badge-magenta">Last Ran: {Utilities.renderReadableDateTime(lastRun.getDateTimeCreated())}</span>
                        )}
                        <p>{module.description}</p>

                        {
                          module.available ? (
                            <button
                              className="btn btn-success mt-1"
                              type="button"
                              onClick={(e) => startPlan(module.name)}
                            >
                              {(processLoading && processName === module.name) && (
                                <i className="fas fa-spinner fa-spin mr-1"></i>
                              )}
                              Use Module
                            </button>
                          ) : (
                            <button
                              className="btn btn-danger mt-1"
                              type="button"
                              disabled={true}
                            >
                              Coming Soon
                            </button>
                          )
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )

        )}
        {
          processTab === CommissionStatementFormProcessTab.PREVIOUS_RUNS && (
            <>
              {commissionStatementResultsLoading && (
                <p className="alert alert-info">
                  <i className="fas fa-spinner fa-spin" /> Loading
                </p>
              )}
              {commissionStatementResults?.length ? (
                <div id="commission-statement-list">
                  {commissionStatementResults.map((result, key) => {
                    const moduleName = getModuleName(result.getModule());
                    const classList = ['border', 'rounded-1'];

                    if (key !== 0) {
                      classList.push('mt-2');
                    }

                    return (
                      <React.Fragment key={result.getId()}>
                        <div className={classList.join(' ')}>
                          <h6 className="mb-0">
                            <button
                              className="h6 btn btn-link collapsed d-block w-100 font-weight-bold text-left"
                              data-toggle="collapse"
                              data-target={`#collapse-statement-${result.getId()}`}
                              aria-expanded="true"
                              aria-controls={`collapse-statement-${result.getId()}`}
                            >
                              {moduleName}
                              <span className="badge badge-magenta badge-pill ml-1">{Utilities.getTimeSince(result.getDateTimeCreated())}</span>
                            </button>
                          </h6>

                          <div
                            id={`collapse-statement-${result.getId()}`}
                            className="collapse"
                            aria-labelledby={`heading${result.getId()}`}
                            data-parent="#commission-statement-list"
                          >
                            <div className="card-body pt-2">
                              <CommissionStatementProcessingResult
                                key={result.getId()}
                                module={result.getModule()}
                                data={result.getData()}
                                mode="execute"
                              />
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    )
                  })}
                </div>
              ) : (
                <p className="alert alert-info mb-0">No previous runs found.</p>
              )}
            </>
          )
        }
      </div >
    </>
  );
};
